import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import StorefrontIcon from "@mui/icons-material/Storefront";

/* Third chat in the set, so it wears the same skin and speaks from the same
   string table as registration and editing. */
import "../RegisterChat/RegisterChat.css";
import STRINGS from "../RegisterChat/chatLang";
import {
  SOCIAL_FIELDS,
  FEATURE_STEP_FLAG,
  unlockedByUpgrade,
} from "../RegisterChat/planFeatures";
import { nowTime } from "../RegisterChat/chatHelpers";
import {
  getMyBussiness,
  getSubcriptionPlan,
  getCurrentPlan,
  createSubscriptionTransaction,
  verifySubscriptionPayment,
} from "../../store/Bussiness/bussinessUrl";

const LANG_KEY = "mc_edit_chat_lang";

const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const RAZORPAY_SCRIPT_ID = "mc-razorpay-checkout";

/* Activation runs through Celery after the payment is captured, so the new
   plan is not on the business the moment checkout closes. */
const ACTIVATION_TIMEOUT_MS = 60000;
const ACTIVATION_POLL_MS = 3000;

const sleep = (ms) => new Promise((r) => window.setTimeout(r, ms));

/* Razorpay's script is only fetched when someone actually reaches the payment
   step — no reason to load a third-party script for people just browsing. */
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById(RAZORPAY_SCRIPT_ID);
    if (existing) {
      /* A second tap while the first load is still in flight must not add a
         second script tag. */
      existing.addEventListener("load", () => resolve(Boolean(window.Razorpay)));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const el = document.createElement("script");
    el.id = RAZORPAY_SCRIPT_ID;
    el.src = RAZORPAY_SRC;
    el.onload = () => resolve(Boolean(window.Razorpay));
    el.onerror = () => resolve(false);
    document.body.appendChild(el);
  });

/* Prices come back as floats; show whole rupees unless there are real paise. */
const money = (amount) => {
  const n = Number(amount) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

const UpgradeChat = () => {
  const history = useHistory();

  const [lang, setLang] = useState(() =>
    localStorage.getItem(LANG_KEY) === "en" ? "en" : "ml"
  );
  const t = STRINGS[lang] || STRINGS.ml;

  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("loading");
  const [biz, setBiz] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  /* Held for the closing screen: what the new plan turned on. */
  const [unlocked, setUnlocked] = useState({ steps: [], counts: [] });

  const threadRef = useRef(null);
  /* Checkout callbacks and the activation poll both outlive the render that
     started them, and firing setState after a back-navigation would warn and
     leak. */
  const mountedRef = useRef(true);
  const bizRef = useRef(null);
  const currentFeaturesRef = useRef(null);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch (e) {
      /* Private mode — the toggle still works for this visit. */
    }
  }, [lang]);

  /* ── Message helpers ── */

  const pushMsg = useCallback((msg) => {
    if (!mountedRef.current) return;
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        ts: nowTime(),
        ...msg,
      },
    ]);
  }, []);

  const pushBot = useCallback(
    (text, delay = 400) => {
      if (!text) return;
      setTyping(true);
      window.setTimeout(() => {
        if (!mountedRef.current) return;
        setTyping(false);
        pushMsg({ from: "bot", text });
      }, delay);
    },
    [pushMsg]
  );

  const pushMe = useCallback((text) => pushMsg({ from: "me", text }), [pushMsg]);

  const pushError = useCallback(
    (text) => pushMsg({ from: "bot", text, error: true }),
    [pushMsg]
  );

  const apiMessage = useCallback(
    (err) =>
      err?.data?.message ||
      err?.response?.data?.message ||
      err?.data?.detail ||
      "",
    []
  );

  /* ── Loading ── */

  const loadAll = useCallback(async () => {
    setStep("loading");
    pushMsg({ from: "bot", text: t.upgradeGreet });
    if (!localStorage.getItem("accessToken")) {
      setStep("noAuth");
      pushBot(t.noAuth, 400);
      return;
    }
    pushBot(t.loadingPlans, 300);
    try {
      const mine = await getMyBussiness();
      const first = mine?.data?.results?.[0];
      if (!first) {
        setStep("noBusiness");
        pushBot(t.noBusiness, 500);
        return;
      }
      bizRef.current = first;
      setBiz(first);

      const planRes = await getCurrentPlan(first.id);
      const active = planRes?.data?.selected_plan;
      /* selected_plan is nullable, and when it is null the API leaves it as a
         bare id rather than an object. */
      const activePlan = active && typeof active === "object" ? active : null;
      setCurrentPlan(activePlan);
      currentFeaturesRef.current = activePlan?.subscription_features || null;

      const listRes = await getSubcriptionPlan(first.id);
      const all = listRes?.data?.results;
      /* The interceptor resolves undefined for 401/403/404 and network faults
         instead of rejecting, so a missing body is a failure, not an empty
         plan list. */
      if (!all) throw new Error("no-response");

      const currentValue = activePlan?.subscription_type?.value ?? 0;
      const buyable = all
        .filter((p) => !p.current_plan && !p.is_basic && Number(p.amount) > 0)
        /* Only genuine upgrades: a lower plan would not start until the
           current one expires, which is not what this chat is for. */
        .filter((p) => (p.subscription_type?.value ?? 0) > currentValue)
        .sort(
          (a, b) =>
            (a.subscription_type?.value ?? 0) - (b.subscription_type?.value ?? 0)
        );

      if (!mountedRef.current) return;
      setPlans(buyable);
      pushBot(
        activePlan ? t.currentPlanIs(activePlan.name) : t.noCurrentPlan,
        450
      );
      if (!buyable.length) {
        setStep("noPlans");
        pushBot(t.noPlans, 900);
        return;
      }
      setStep("plans");
      window.setTimeout(() => showPlansRef.current(), 950);
    } catch (err) {
      if (!mountedRef.current) return;
      setStep("loadFailed");
      pushError(apiMessage(err) || t.plansFailed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiMessage, pushBot, pushError, pushMsg, t]);

  /* ── Plan card ── */

  const showPlans = useCallback(() => {
    setTyping(true);
    window.setTimeout(() => {
      if (!mountedRef.current) return;
      setTyping(false);
      const stamp = Date.now();
      setMessages((prev) => [
        /* Only ever one plan card in the thread; an older copy would still
           have live Choose buttons. */
        ...prev.filter((m) => !m.plans && !m.plansIntro),
        {
          id: `pi_${stamp}`,
          ts: nowTime(),
          from: "bot",
          text: t.plansIntro,
          plansIntro: true,
        },
        { id: `pc_${stamp}`, ts: nowTime(), from: "bot", plans: true },
      ]);
    }, 380);
  }, [t]);

  const showPlansRef = useRef(showPlans);
  useEffect(() => {
    showPlansRef.current = showPlans;
  }, [showPlans]);

  /* Turns a feature diff into a line the owner can actually read. */
  const describe = useCallback(
    ({ steps, counts }) => {
      const out = [];
      steps.forEach((id) => {
        if (id === "catalogueMenu" || id === "coverMenu" || id === "serviceMenu") {
          return; /* Covered by the counts below, with the number. */
        }
        if (SOCIAL_FIELDS[id]) {
          out.push(t.socialLabels[SOCIAL_FIELDS[id]]);
        } else if (FEATURE_STEP_FLAG[id]) {
          out.push(t.labels[id === "about" ? "about" : id]);
        }
      });
      counts.forEach(({ kind, extra }) => {
        const what =
          kind === "catalogue"
            ? t.labels.catalogue
            : kind === "cover"
            ? t.labels.cover
            : t.labels.services;
        out.push(t.unlockedCount(extra, what));
      });
      return out;
    },
    [t]
  );

  const planRows = useMemo(
    () =>
      plans.map((p) => ({
        plan: p,
        adds: describe(
          unlockedByUpgrade(currentFeaturesRef.current, p.subscription_features)
        ),
      })),
    [plans, describe]
  );

  /* ── Payment ── */

  const choosePlan = (plan) => {
    setChosen(plan);
    setStep("confirm");
    pushMe(plan.name);
    pushBot(t.confirmPay(plan.name, money(plan.amount)), 350);
  };

  const backToPlans = () => {
    setChosen(null);
    setStep("plans");
    pushMe(t.cancel);
    window.setTimeout(() => showPlansRef.current(), 400);
  };

  /* Polls until the purchased plan is the one on the business. Activation is
     a Celery hop, so it is never instant and can occasionally not happen at
     all — the caller must handle null. */
  const waitForActivation = useCallback(async (planId) => {
    const deadline = Date.now() + ACTIVATION_TIMEOUT_MS;
    while (Date.now() < deadline && mountedRef.current) {
      await sleep(ACTIVATION_POLL_MS);
      try {
        const res = await getCurrentPlan(bizRef.current.id);
        const sp = res?.data?.selected_plan;
        if (sp && typeof sp === "object" && sp.id === planId) return sp;
      } catch (e) {
        /* A blip mid-poll is not a failure; keep trying until the deadline. */
      }
    }
    return null;
  }, []);

  const finishAfterPayment = useCallback(
    async (plan) => {
      setStep("activating");
      pushBot(t.activating, 300);
      const active = await waitForActivation(plan.id);
      if (!mountedRef.current) return;

      if (!active) {
        /* The money is taken and the server has it — say so plainly rather
           than implying the payment failed. */
        setStep("slow");
        pushBot(t.activationSlow, 400);
        return;
      }

      setCurrentPlan(active);
      pushBot(t.activated(active.name), 350);

      const diff = unlockedByUpgrade(
        currentFeaturesRef.current,
        active.subscription_features
      );
      currentFeaturesRef.current = active.subscription_features || null;
      setUnlocked(diff);

      const lines = describe(diff);
      if (!lines.length) {
        setStep("done");
        pushBot(t.unlockedNothing, 900);
        return;
      }
      setStep("unlocked");
      pushBot(`${t.unlockedIntro}\n${lines.map((l) => `• ${l}`).join("\n")}`, 950);
    },
    [describe, pushBot, t, waitForActivation]
  );

  const startPayment = async () => {
    const plan = chosen;
    if (!plan || busy) return;
    setBusy(true);
    pushMe(t.payNow);
    pushBot(t.openingPayment, 250);
    try {
      const ready = await loadRazorpay();
      if (!ready) throw new Error("razorpay-unavailable");

      const res = await createSubscriptionTransaction({
        businessId: bizRef.current.id,
        planId: plan.id,
        amount: plan.amount,
      });
      const order = res?.data;
      if (!order?.order_id || !order?.acc_key) throw new Error("no-order");

      setStep("paying");
      const rzp = new window.Razorpay({
        key: order.acc_key,
        amount: order.amount,
        currency: "INR",
        name: "Master Click",
        description: plan.name,
        order_id: order.order_id,
        prefill: { contact: bizRef.current?.mobile_number || "" },
        theme: { color: "#008069" },
        handler: async (response) => {
          /* Razorpay calling back proves the widget closed, nothing more. The
             plan is only treated as bought once the server has verified the
             signature and captured the payment. */
          try {
            const verified = await verifySubscriptionPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (!verified?.data?.status) throw new Error("not-verified");
            if (!mountedRef.current) return;
            pushBot(t.paymentOk, 250);
            finishAfterPayment(plan);
          } catch (err) {
            if (!mountedRef.current) return;
            setStep("confirm");
            pushError(t.paymentFailed(apiMessage(err)));
          } finally {
            if (mountedRef.current) setBusy(false);
          }
        },
        modal: {
          ondismiss: () => {
            /* Without this the chat would sit on "opening payment…" forever
               when someone closes the widget. */
            if (!mountedRef.current) return;
            setBusy(false);
            setStep("confirm");
            pushBot(t.paymentCancelled, 250);
          },
        },
      });
      rzp.on("payment.failed", (resp) => {
        if (!mountedRef.current) return;
        setBusy(false);
        setStep("confirm");
        pushError(t.paymentFailed(resp?.error?.description || ""));
      });
      rzp.open();
      /* busy stays true while the widget is open; every exit from it — success,
         failure, dismiss — clears it. */
    } catch (err) {
      if (!mountedRef.current) return;
      setBusy(false);
      setStep("confirm");
      pushError(t.paymentFailed(apiMessage(err)));
    }
  };

  /* Hands the newly unlocked fields to the edit chat rather than growing a
     second copy of the same question engine in here. */
  const fillNow = () => {
    history.push("/edit-chat", { focus: unlocked.steps });
  };

  /* ── Start ── */

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, step]);

  /* ── Composer ── */

  const renderComposer = () => {
    if (step === "loading" || step === "paying" || step === "activating") {
      return null;
    }

    if (step === "noAuth") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => history.push("/sign-in")}>
            {t.signInBtn}
          </button>
        </div>
      );
    }

    if (step === "noBusiness") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => history.push("/register-chat")}>
            {t.registerBtn}
          </button>
        </div>
      );
    }

    if (step === "loadFailed") {
      return (
        <div className="rc-cta">
          <button
            className="solid"
            onClick={() => {
              setMessages([]);
              loadAll();
            }}
          >
            {t.retry}
          </button>
        </div>
      );
    }

    if (step === "noPlans" || step === "slow" || step === "done") {
      return (
        <div className="rc-cta">
          <button className="solid" onClick={() => history.push("/edit-chat")}>
            {t.edit}
          </button>
          <button className="outline" onClick={() => history.push("/my-list")}>
            {t.myListingsBtn}
          </button>
        </div>
      );
    }

    if (step === "unlocked") {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" onClick={fillNow}>
            {t.fillNow}
          </button>
          <button
            className="rc-chip ghost"
            onClick={() => {
              setStep("done");
              pushMe(t.later);
            }}
          >
            {t.later}
          </button>
        </div>
      );
    }

    if (step === "confirm") {
      return (
        <div className="rc-chips">
          <button className="rc-chip primary" disabled={busy} onClick={startPayment}>
            {busy ? t.openingPayment : t.payNow}
          </button>
          <button className="rc-chip ghost" disabled={busy} onClick={backToPlans}>
            {t.cancel}
          </button>
        </div>
      );
    }

    /* step === "plans": the card's own Choose buttons drive this step. */
    return (
      <div className="rc-cta">
        <button className="outline" onClick={() => history.push("/my-list")}>
          {t.myListingsBtn}
        </button>
      </div>
    );
  };

  /* ── Plan card ── */

  const renderPlans = () => (
    <div className="rc-review">
      {planRows.map(({ plan, adds }) => (
        <div className="rc-review-row" key={plan.id}>
          <div className="rc-review-label">
            {plan.name}
            <div style={{ fontWeight: 400, marginTop: 2 }}>
              ₹{money(plan.amount)} / {plan.duration} {plan.duration_unit}
            </div>
          </div>
          <div className="rc-review-value">
            {adds.length ? (
              <>
                {t.planAdds}
                <br />
                {adds.map((a, i) => (
                  <span key={i}>
                    • {a}
                    <br />
                  </span>
                ))}
              </>
            ) : (
              <span className="empty">{t.planNothingExtra}</span>
            )}
          </div>
          <button
            className="rc-review-edit"
            disabled={busy}
            onClick={() => choosePlan(plan)}
          >
            {t.choose}
          </button>
        </div>
      ))}
    </div>
  );

  /* ── Render ── */

  return (
    <div className="rc-root">
      <div className="rc-header">
        <button className="rc-back" onClick={() => history.push("/my-list")}>
          <ArrowBackIcon fontSize="small" />
        </button>
        <div className="rc-avatar">
          <StorefrontIcon fontSize="small" />
        </div>
        <div className="rc-header-text">
          <div className="rc-header-name">{biz?.name || t.headerName}</div>
          <div className="rc-header-status">
            {typing ? t.typing : currentPlan?.name || t.headerStatus}
          </div>
        </div>
        <button
          className="rc-lang-toggle"
          onClick={() => setLang((l) => (l === "ml" ? "en" : "ml"))}
        >
          {lang === "ml" ? "EN" : "മല"}
        </button>
      </div>

      <div className="rc-thread" ref={threadRef}>
        <div className="rc-thread-inner">
          <div className="rc-day-pill">{t.today}</div>

          {messages.map((m, i) => {
            const prev = messages[i - 1];
            const tail = !prev || prev.from !== m.from;

            if (m.plans) {
              return (
                <div className="rc-row" key={m.id}>
                  {renderPlans()}
                </div>
              );
            }

            return (
              <div className={`rc-row ${m.from}`} key={m.id}>
                <div
                  className={`rc-bubble ${m.from} ${tail ? "tail" : ""} ${
                    m.error ? "error" : ""
                  }`}
                >
                  {m.text}
                  <span className="rc-meta">
                    {m.ts}
                    {m.from === "me" && (
                      <DoneAllIcon className="rc-tick" style={{ fontSize: 14 }} />
                    )}
                  </span>
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="rc-row">
              <div className="rc-bubble bot tail">
                <div className="rc-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="rc-composer">
        <div className="rc-composer-inner">{renderComposer()}</div>
      </div>
    </div>
  );
};

export default UpgradeChat;
